import rules from '../../../game-server/modules/ponder-flight-game/data/airline-4x-rules.json';
export const Airline4XRules = rules;
/**
 * Server Constants
 * 
 * IMPORTANT: These values must match the server-side constants in:
 * game-server/modules/ponder-flight-game/constants.go
 * 
 * If server values change, update this file to match.
 */

// Initial values for teams
export const InitialCash = 500.0; // Initial cash in millions
export const InitialInfluence = rules.initial_influence; // Initial influence points
export const InitialKnowledgePoints = 0; // Initial knowledge points

// ==================== NETWORK OVERHEAD SIGMOID ====================
// Must match NetworkOverhead* constants in network-overhead.go

/** Plateau marginal rate per effective-city per tick ($M) */
export const NetworkOverheadMaxRate = 2.5;

/** Effective network size where the sigmoid is steepest */
export const NetworkOverheadMidpoint = 60.0;

/** Sigmoid steepness — plateau (95%) at effective size ~100 */
export const NetworkOverheadSteepness = 0.075;

/** Desirability weight table: maps desirability score (1-5) to network size weight */
export const DesirabilityWeights: Record<number, number> = {
    1: 0.5,
    2: 0.75,
    3: 1.0,
    4: 1.5,
    5: 2.0,
};

/** Get the network size weight for a city's desirability score */
export const getDesirabilityWeight = (desirabilityScore: number): number => {
    return DesirabilityWeights[desirabilityScore] ?? 1.0;
};

/** Sigmoid marginal rate at a given effective network size */
function sigmoidMarginalRate(effectiveSize: number): number {
    return NetworkOverheadMaxRate / (1.0 + Math.exp(-NetworkOverheadSteepness * (effectiveSize - NetworkOverheadMidpoint)));
}

/**
 * Calculate total per-tick network overhead for a given effective network size.
 * Uses trapezoidal numerical integration of the sigmoid marginal rate curve.
 * Returns overhead in $M.
 */
export function calculateNetworkOverhead(effectiveSize: number): number {
    if (effectiveSize <= 0) return 0;
    const steps = 200;
    const dx = effectiveSize / steps;
    let total = 0;
    for (let i = 0; i < steps; i++) {
        const x0 = i * dx;
        const x1 = (i + 1) * dx;
        total += (sigmoidMarginalRate(x0) + sigmoidMarginalRate(x1)) / 2.0 * dx;
    }
    return total;
}

/**
 * Calculate the marginal per-tick overhead from adding one city with the given
 * desirability weight to a network of the given effective size.
 * The globe's signpost says it, a year's worth, over a city you could open.
 */
export function calculateMarginalOverhead(currentEffectiveSize: number, additionalWeight: number): number {
    return calculateNetworkOverhead(currentEffectiveSize + additionalWeight) - calculateNetworkOverhead(currentEffectiveSize);
}

/**
 * The effective size of an airline's network: each city its routes serve,
 * weighted by its desirability score — a city the data does not know counts
 * as one, as on the server (CalculateEffectiveNetworkSize). Takes the served
 * cities' scores, undefined for a city the data does not know.
 */
export function calculateEffectiveNetworkSize(desirabilityScores: Iterable<number | undefined>): number {
    let size = 0;
    for (const score of desirabilityScores) {
        size += score === undefined ? 1.0 : getDesirabilityWeight(score);
    }
    return size;
}
